import { ALL_FORMATS, BlobSource, Input, StreamTarget, type StreamTargetChunk } from 'mediabunny';
import { sha256Stream } from './sha256';

const WORKSPACE_LOCK = 'bitpop-lab-workspace';
const WORKSPACE_DIRECTORY = 'workspace';

export type WorkspaceErrorCode =
  | 'WORKSPACE_UNSUPPORTED'
  | 'WORKSPACE_LOCK_UNSUPPORTED'
  | 'WORKSPACE_BUSY'
  | 'WORKSPACE_VALIDATION_FAILED'
  | 'WORKSPACE_QUOTA_EXCEEDED';

export class WorkspaceError extends Error {
  constructor(readonly code: WorkspaceErrorCode, message: string) {
    super(message);
    this.name = 'WorkspaceError';
  }
}

export type WorkspaceInventory = {
  jobs: string[];
  readyExports: string[];
};

export type ReadyArtifact = {
  exportId: string;
  file: File;
  sizeBytes: number;
  sha256: string;
  durationSeconds: number;
};

export interface RandomAccessOutput {
  write(position: number, data: Uint8Array<ArrayBuffer>): Promise<void>;
  truncate(size: number): Promise<void>;
  flush(): Promise<void>;
  close(): Promise<void>;
  abort(reason?: unknown): Promise<void>;
}

export type WorkspaceJob = {
  jobId: string;
  target: StreamTarget;
  firstWrite: Promise<void>;
  hasTargetWriteFailure(): boolean;
  publish(expectedDurationSeconds: number): Promise<ReadyArtifact>;
  abort(): Promise<void>;
};

type WorkspaceOptions = {
  maxWriteBytes?: number;
  onFirstWrite?: () => void;
  onBeforeReady?: () => void;
};

class OpfsRandomAccessOutput implements RandomAccessOutput {
  private writtenBytes = 0;
  private closed = false;

  constructor(
    private readonly writable: FileSystemWritableFileStream,
    private readonly options: WorkspaceOptions,
  ) {}

  async write(position: number, data: Uint8Array<ArrayBuffer>): Promise<void> {
    if (this.closed) throw new Error('The OPFS output is already closed.');
    if (this.options.maxWriteBytes !== undefined && this.writtenBytes + data.byteLength > this.options.maxWriteBytes) {
      throw new WorkspaceError('WORKSPACE_QUOTA_EXCEEDED', 'The test output quota was exhausted.');
    }
    await this.writable.write({ type: 'write', position, data });
    this.writtenBytes += data.byteLength;
    if (data.byteLength > 0 && this.options.onFirstWrite) {
      const callback = this.options.onFirstWrite;
      this.options.onFirstWrite = undefined;
      callback();
    }
  }

  async truncate(size: number): Promise<void> {
    await this.writable.truncate(size);
  }

  async flush(): Promise<void> {
    // FileSystemWritableFileStream resolves each write before the next StreamTarget write is accepted.
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    await this.writable.close();
  }

  async abort(reason?: unknown): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    await this.writable.abort(reason);
  }
}

function assertWorkspaceApi(): void {
  if (!navigator.storage?.getDirectory) {
    throw new WorkspaceError('WORKSPACE_UNSUPPORTED', 'OPFS is unavailable; the laboratory refuses to write without it.');
  }
  if (!('locks' in navigator)) {
    throw new WorkspaceError('WORKSPACE_LOCK_UNSUPPORTED', 'Web Locks is unavailable; the laboratory refuses concurrent writing.');
  }
}

async function directory(parent: FileSystemDirectoryHandle, name: string): Promise<FileSystemDirectoryHandle> {
  return parent.getDirectoryHandle(name, { create: true });
}

async function file(parent: FileSystemDirectoryHandle, name: string): Promise<FileSystemFileHandle> {
  return parent.getFileHandle(name, { create: true });
}

async function childNames(parent: FileSystemDirectoryHandle): Promise<string[]> {
  const names: string[] = [];
  for await (const [name] of parent.entries()) names.push(name);
  return names.sort();
}

async function copyAndHash(source: File, destination: FileSystemFileHandle): Promise<{ sizeBytes: number; sha256: string }> {
  const writable = await destination.createWritable();
  const reader = source.stream().getReader();
  let sizeBytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      sizeBytes += value.byteLength;
      await writable.write(value);
    }
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  } finally {
    reader.releaseLock();
  }
  // Re-read the persisted file. This verifies the destination, without retaining the source artifact in memory.
  const copied = await destination.getFile();
  return { sizeBytes, sha256: await sha256Stream(copied.stream()) };
}

async function validateWebm(fileToValidate: File, expectedDurationSeconds: number): Promise<number> {
  if (fileToValidate.size === 0) {
    throw new WorkspaceError('WORKSPACE_VALIDATION_FAILED', 'The temporary WebM is empty.');
  }
  const input = new Input({ source: new BlobSource(fileToValidate), formats: ALL_FORMATS });
  try {
    const [video, audio] = await Promise.all([input.getVideoTracks(), input.getAudioTracks()]);
    if (video.length !== 1 || audio.length !== 1) {
      throw new WorkspaceError('WORKSPACE_VALIDATION_FAILED', 'The temporary artifact does not contain exactly one video and one audio track.');
    }
    const durationSeconds = await input.computeDuration();
    if (Math.abs(durationSeconds - expectedDurationSeconds) > 0.002) {
      throw new WorkspaceError('WORKSPACE_VALIDATION_FAILED', `Unexpected WebM duration: ${durationSeconds}s.`);
    }
    return durationSeconds;
  } finally {
    input.dispose();
  }
}

export class OpfsWorkspace {
  private constructor(
    private readonly root: FileSystemDirectoryHandle,
    private readonly options: WorkspaceOptions,
  ) {}

  static async open(options: WorkspaceOptions = {}): Promise<OpfsWorkspace> {
    assertWorkspaceApi();
    const opfs = await navigator.storage.getDirectory();
    const root = await directory(opfs, WORKSPACE_DIRECTORY);
    await Promise.all(['sources', 'jobs', 'exports', 'reports'].map((name) => directory(root, name)));
    return new OpfsWorkspace(root, options);
  }

  async runExclusive<T>(operation: (workspace: OpfsWorkspace) => Promise<T>): Promise<T> {
    const locks = navigator.locks;
    return locks.request(WORKSPACE_LOCK, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
      if (!lock) throw new WorkspaceError('WORKSPACE_BUSY', 'Another laboratory job currently owns the workspace lock.');
      await this.cleanupInterruptedJobs();
      return operation(this);
    });
  }

  async inventory(): Promise<WorkspaceInventory> {
    const [jobs, exports] = await Promise.all([
      directory(this.root, 'jobs'),
      directory(this.root, 'exports'),
    ]);
    const readyExports: string[] = [];
    for await (const [name, handle] of exports.entries()) {
      if (handle.kind !== 'directory') continue;
      try {
        const manifest = await (await handle.getFileHandle('artifact.json')).getFile();
        const value = JSON.parse(await manifest.text()) as { status?: unknown };
        if (value.status === 'READY') readyExports.push(name);
      } catch {
        // An export without a valid READY marker is deliberately invisible.
      }
    }
    return { jobs: await childNames(jobs), readyExports: readyExports.sort() };
  }

  async writeReport(runId: string, report: unknown): Promise<void> {
    const reports = await directory(this.root, 'reports');
    const handle = await file(reports, `${runId}.json`);
    const writable = await handle.createWritable();
    try {
      await writable.write(JSON.stringify(report, null, 2));
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => undefined);
      throw error;
    }
  }

  async readReport<T>(runId: string): Promise<T | null> {
    const reports = await directory(this.root, 'reports');
    try {
      const handle = await reports.getFileHandle(`${runId}.json`);
      return JSON.parse(await (await handle.getFile()).text()) as T;
    } catch {
      return null;
    }
  }

  async listReports(): Promise<Array<{ runId: string; file: File }>> {
    const reports = await directory(this.root, 'reports');
    const result: Array<{ runId: string; file: File }> = [];
    for await (const [name, handle] of reports.entries()) {
      if (handle.kind !== 'file' || !name.endsWith('.json')) continue;
      result.push({ runId: name.slice(0, -'.json'.length), file: await handle.getFile() });
    }
    return result.sort((left, right) => left.runId.localeCompare(right.runId));
  }

  async readReadyArtifact(exportId: string): Promise<ReadyArtifact | null> {
    const exports = await directory(this.root, 'exports');
    try {
      const exportDirectory = await exports.getDirectoryHandle(exportId);
      const manifest = JSON.parse(await (await (await exportDirectory.getFileHandle('artifact.json')).getFile()).text()) as {
        status?: unknown;
        exportId?: unknown;
        sizeBytes?: unknown;
        sha256?: unknown;
        durationSeconds?: unknown;
      };
      if (manifest.status !== 'READY' || manifest.exportId !== exportId || typeof manifest.sizeBytes !== 'number'
        || typeof manifest.sha256 !== 'string' || typeof manifest.durationSeconds !== 'number') return null;
      const artifact = await (await exportDirectory.getFileHandle('file.webm')).getFile();
      if (artifact.size !== manifest.sizeBytes) return null;
      return {
        exportId,
        file: artifact,
        sizeBytes: manifest.sizeBytes,
        sha256: manifest.sha256,
        durationSeconds: manifest.durationSeconds,
      };
    } catch {
      return null;
    }
  }

  async listReadyArtifacts(): Promise<ReadyArtifact[]> {
    const { readyExports } = await this.inventory();
    const artifacts = await Promise.all(readyExports.map((exportId) => this.readReadyArtifact(exportId)));
    return artifacts.filter((artifact): artifact is ReadyArtifact => artifact !== null);
  }

  async createJob(jobId: string): Promise<WorkspaceJob> {
    const jobs = await directory(this.root, 'jobs');
    const jobDirectory = await directory(jobs, jobId);
    const partHandle = await file(jobDirectory, 'output.webm.part');
    const output = new OpfsRandomAccessOutput(await partHandle.createWritable(), { ...this.options });
    let resolveFirstWrite: (() => void) | null = null;
    let targetWriteFailed = false;
    const firstWrite = new Promise<void>((resolve) => { resolveFirstWrite = resolve; });
    const stream = new WritableStream<StreamTargetChunk>({
      write: async (chunk) => {
        try {
          await output.write(chunk.position, chunk.data);
          if (chunk.data.byteLength > 0 && resolveFirstWrite) {
            resolveFirstWrite();
            resolveFirstWrite = null;
          }
        } catch (error) {
          targetWriteFailed = true;
          throw error;
        }
      },
      close: async () => output.close(),
      abort: async (reason) => output.abort(reason),
    });
    const target = new StreamTarget(stream);
    let settled = false;
    const removeJob = async (): Promise<void> => {
      if (settled) return;
      let lastError: unknown = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await jobs.removeEntry(jobId, { recursive: true });
          settled = true;
          return;
        } catch (error) {
          lastError = error;
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
      }
      throw lastError instanceof Error ? lastError : new Error('Unable to remove the interrupted workspace job.');
    };

    return {
      jobId,
      target,
      firstWrite,
      hasTargetWriteFailure: () => targetWriteFailed,
      publish: async (expectedDurationSeconds) => {
        const part = await partHandle.getFile();
        const durationSeconds = await validateWebm(part, expectedDurationSeconds);
        const sourceHash = await sha256Stream(part.stream());
        const exports = await directory(this.root, 'exports');
        const exportDirectory = await directory(exports, jobId);
        const finalHandle = await file(exportDirectory, 'file.webm');
        const copied = await copyAndHash(part, finalHandle);
        if (copied.sizeBytes !== part.size || copied.sha256 !== sourceHash) {
          await exports.removeEntry(jobId, { recursive: true }).catch(() => undefined);
          throw new WorkspaceError('WORKSPACE_VALIDATION_FAILED', 'The published WebM did not match its validated temporary file.');
        }
        this.options.onBeforeReady?.();
        const manifestHandle = await file(exportDirectory, 'artifact.json');
        const manifest = {
          schemaVersion: 1,
          status: 'READY',
          exportId: jobId,
          fileName: 'file.webm',
          sizeBytes: copied.sizeBytes,
          sha256: copied.sha256,
          durationSeconds,
        };
        const manifestWritable = await manifestHandle.createWritable();
        await manifestWritable.write(JSON.stringify(manifest, null, 2));
        await manifestWritable.close();
        await removeJob();
        return { exportId: jobId, file: await finalHandle.getFile(), sizeBytes: copied.sizeBytes, sha256: copied.sha256, durationSeconds };
      },
      abort: async () => {
        // A StreamTarget write failure leaves its WritableStream errored. Closing its
        // writer again can itself reject, so abort the raw OPFS writer directly first.
        await output.abort().catch(() => undefined);
        await removeJob();
      },
    };
  }

  private async cleanupInterruptedJobs(): Promise<void> {
    const jobs = await directory(this.root, 'jobs');
    for await (const [name] of jobs.entries()) {
      await jobs.removeEntry(name, { recursive: true });
    }
  }
}
